import { useTranslations } from "next-intl";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { useLoginRequired } from "@/hooks/auth/useLoginRequired";
import { useAddFavorite, useRemoveFavorite } from "./useFavoriteMutations";
import { useFavoritesLookup } from "./useFavoritesLookup";

const FAVORITE_ERROR_TOAST_ID = "favorite-error-toast";

export const useFavoriteToggle = (productId: string) => {
  const t = useTranslations("favorites");
  const handleLoginRequired = useLoginRequired();
  const { set, refetch } = useFavoritesLookup();

  const serverFavorited = set.has(productId);
  const [override, setOverride] = useState<boolean | null>(null);
  const isFavorited = override ?? serverFavorited;

  useEffect(() => {
    if (override !== null && override === serverFavorited) {
      setOverride(null);
    }
  }, [override, serverFavorited]);

  const onError = useCallback(
    (previous: boolean) => {
      setOverride(previous);
      toast.error(t("toggleError"), { id: FAVORITE_ERROR_TOAST_ID });
    },
    [t],
  );

  const addFavorite = useAddFavorite({ onSuccess: refetch });
  const removeFavorite = useRemoveFavorite({ onSuccess: refetch });

  const toggle = useCallback(() => {
    handleLoginRequired(() => {
      const previous = isFavorited;
      const next = !previous;
      setOverride(next);
      const params = { params: { path: { productId } } };
      if (next) {
        addFavorite.mutate(params, { onError: () => onError(previous) });
      } else {
        removeFavorite.mutate(params, { onError: () => onError(previous) });
      }
    })();
  }, [
    handleLoginRequired,
    isFavorited,
    productId,
    addFavorite,
    removeFavorite,
    onError,
  ]);

  return {
    isFavorited,
    toggle,
    isPending: addFavorite.isPending || removeFavorite.isPending,
  };
};
